
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, Coffee, Users, Gamepad2 } from "lucide-react";

interface EventTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  category: string;
  options: Array<{
    type: 'date' | 'time' | 'location';
    values: string[];
  }>;
  suggestedGuests?: string[];
}

const templates: EventTemplate[] = [
  {
    id: 'team-meeting',
    name: 'Team Meeting',
    description: 'Schedule a team meeting with multiple time options',
    icon: Users,
    category: 'Work',
    options: [
      {
        type: 'date',
        values: [
          new Date(Date.now() + 86400000).toISOString().split('T')[0], // Tomorrow
          new Date(Date.now() + 172800000).toISOString().split('T')[0], // Day after
          new Date(Date.now() + 259200000).toISOString().split('T')[0], // 3 days
        ]
      },
      {
        type: 'time',
        values: ['09:00', '10:00', '14:00', '15:00']
      }
    ],
    suggestedGuests: []
  },
  {
    id: 'coffee-meetup',
    name: 'Coffee Meetup',
    description: 'Casual coffee meeting with location and time options',
    icon: Coffee,
    category: 'Social',
    options: [
      {
        type: 'location',
        values: ['Starbucks Downtown', 'Blue Bottle Coffee']
      },
      {
        type: 'time',
        values: ['08:00', '09:00', '15:00', '16:00']
      }
    ],
    suggestedGuests: []
  },
  {
    id: 'game-night',
    name: 'Game Night',
    description: 'Plan a fun game night with friends',
    icon: Gamepad2,
    category: 'Entertainment',
    options: [
      {
        type: 'date',
        values: [
          new Date(Date.now() + 86400000 * 5).toISOString().split('T')[0], // Next Friday
          new Date(Date.now() + 86400000 * 6).toISOString().split('T')[0], // Next Saturday
        ]
      },
      {
        type: 'location',
        values: ['My Place', 'Community Center', 'Board Game Cafe']
      },
      {
        type: 'time',
        values: ['18:00', '19:00', '20:00']
      }
    ],
    suggestedGuests: []
  },
  {
    id: 'client-meeting',
    name: 'Client Meeting',
    description: 'Professional client meeting with flexible scheduling',
    icon: Users,
    category: 'Work',
    options: [
      {
        type: 'date',
        values: [
          new Date(Date.now() + 86400000).toISOString().split('T')[0],
          new Date(Date.now() + 172800000).toISOString().split('T')[0],
          new Date(Date.now() + 259200000).toISOString().split('T')[0],
        ]
      },
      {
        type: 'time',
        values: ['09:00', '10:00', '11:00', '14:00', '15:00']
      },
      {
        type: 'location',
        values: ['Conference Room A', 'Zoom Meeting', 'Client Office']
      }
    ],
    suggestedGuests: []
  },
  {
    id: 'birthday-party',
    name: 'Birthday Party',
    description: 'Celebrate with friends and family',
    icon: Users,
    category: 'Social',
    options: [
      {
        type: 'date',
        values: [
          new Date(Date.now() + 86400000 * 7).toISOString().split('T')[0], // Next week
          new Date(Date.now() + 86400000 * 8).toISOString().split('T')[0],
        ]
      },
      {
        type: 'time',
        values: ['14:00', '15:00', '16:00', '17:00']
      },
      {
        type: 'location',
        values: ['Home', 'Park', 'Restaurant', 'Community Center']
      }
    ],
    suggestedGuests: []
  }
];

interface EventTemplatesProps {
  onSelectTemplate: (template: EventTemplate) => void;
  onClose: () => void;
}

export const EventTemplates: React.FC<EventTemplatesProps> = ({ 
  onSelectTemplate, 
  onClose 
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = Array.from(new Set(templates.map(t => t.category)));
  const filteredTemplates = selectedCategory 
    ? templates.filter(t => t.category === selectedCategory)
    : templates;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Choose a Template</h2>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button
          variant={selectedCategory === null ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedCategory(null)}
        >
          All
        </Button>
        {categories.map(category => (
          <Button
            key={category}
            variant={selectedCategory === category ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </Button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredTemplates.map((template) => {
          const Icon = template.icon;
          return (
            <Card key={template.id} className="hover:shadow-md transition-shadow cursor-pointer">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <Badge variant="secondary" className="mt-1">
                      {template.category}
                    </Badge>
                  </div>
                </div>
                <CardDescription>{template.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  {template.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                      {option.type === 'date' && <Calendar className="w-4 h-4" />}
                      {option.type === 'time' && <Clock className="w-4 h-4" />}
                      {option.type === 'location' && <MapPin className="w-4 h-4" />}
                      <span className="capitalize">{option.type}s: {option.values.length} options</span>
                    </div>
                  ))}
                </div>
                <Button 
                  className="w-full" 
                  onClick={() => onSelectTemplate(template)}
                >
                  Use Template
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
